import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import { ManualConfirmedPair } from '../../types/entry.types';
import {
  Subgroup,
  useAssignPairSubgroup,
  useBulkAssignPairSubgroup
} from '../../api/useSubgroups';
import { sortGroups } from '../../utils/sortGroups';

/**
 * Per-row subgroup picker + bulk "Move selected to subgroup" — mirrors the
 * BucketPage group picker, scoped to ONE opener group's pairs (the rows the
 * EntryPage already narrowed by the active group filter). The per-row Select
 * assigns one pair; the selection bar bulk-moves the checked subset (chunked
 * at 100 inside the hook). `UNASSIGNED` clears the subgroup.
 */

const UNASSIGNED = '__unassigned__';

interface Props {
  groupId: string;
  /** Pairs already narrowed to the active group (and optional search). */
  pairs: ManualConfirmedPair[];
  subgroups: Subgroup[];
}

export function SubgroupAssignPanel({ groupId, pairs, subgroups }: Props) {
  const assignMutation = useAssignPairSubgroup();
  const bulkAssignMutation = useBulkAssignPairSubgroup();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const sortedSubgroups = useMemo(() => sortGroups(subgroups), [subgroups]);

  // The pair set is scoped to the active group; reset the selection whenever
  // the group changes so a bulk move never touches off-screen rows.
  useEffect(() => {
    setSelected(new Set());
  }, [groupId]);

  const selectableIds = useMemo(
    () => pairs.map(p => p._id).filter((id): id is string => !!id),
    [pairs]
  );
  const selectedVisible = useMemo(
    () => pairs.filter(p => selected.has(p._id)),
    [pairs, selected]
  );
  const allSelected =
    selectableIds.length > 0 && selectedVisible.length === selectableIds.length;

  const toggleRow = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(selectableIds));

  const handleAssign = (pairId: string, value: string) =>
    void assignMutation
      .mutateAsync({
        pairId,
        groupId,
        subgroupId: value === UNASSIGNED ? null : value
      })
      .catch(() => {});

  const handleBulkAssign = async (value: string) => {
    if (selectedVisible.length === 0) return;
    await bulkAssignMutation
      .mutateAsync({
        pairIds: selectedVisible.map(p => p._id),
        groupId,
        subgroupId: value === UNASSIGNED ? null : value
      })
      .catch(() => {});
    setSelected(new Set());
  };

  if (pairs.length === 0) {
    return (
      <Typography sx={{ fontSize: '0.75rem', color: 'grey.500', p: 1 }}>
        No pairs in this group to assign.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Bulk move bar — only when a subset is checked. */}
      {selectedVisible.length > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            py: 0.5,
            px: 1,
            mb: 0.5,
            borderRadius: 1,
            backgroundColor: 'action.selected',
            flexShrink: 0
          }}
        >
          <Typography sx={{ fontSize: '0.75rem' }}>
            {selectedVisible.length} selected
          </Typography>
          <Select
            size="small"
            value=""
            displayEmpty
            disabled={bulkAssignMutation.isLoading}
            onChange={e => void handleBulkAssign(e.target.value)}
            sx={{ fontSize: '0.75rem', minWidth: 190, height: 28 }}
            inputProps={{ 'aria-label': 'Move selected to subgroup' }}
            renderValue={() =>
              bulkAssignMutation.isLoading
                ? 'Moving…'
                : 'Move selected to subgroup…'
            }
          >
            <MenuItem value={UNASSIGNED} sx={{ fontSize: '0.75rem' }}>
              Unassigned
            </MenuItem>
            {sortedSubgroups.map(s => (
              <MenuItem key={s._id} value={s._id} sx={{ fontSize: '0.75rem' }}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            onClick={() => setSelected(new Set())}
            sx={{ fontSize: '0.7rem' }}
          >
            Clear
          </Button>
        </Stack>
      )}

      <TableContainer sx={{ maxHeight: 280, minHeight: 0 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={selectedVisible.length > 0 && !allSelected}
                  onChange={toggleAll}
                  inputProps={{ 'aria-label': 'Select all pairs' }}
                />
              </TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>Market</TableCell>
              <TableCell sx={{ fontSize: '0.7rem' }}>Subgroup</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pairs.map(pair => (
              <TableRow key={pair._id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selected.has(pair._id)}
                    onChange={() => toggleRow(pair._id)}
                    inputProps={{
                      'aria-label': `Select ${pair.pairName}`
                    }}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Typography sx={{ fontSize: '0.78rem' }} noWrap>
                    {pair.pairName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={pair.openerSubgroupId ?? UNASSIGNED}
                    onChange={e => handleAssign(pair._id, e.target.value)}
                    sx={{ fontSize: '0.75rem', minWidth: 150, height: 26 }}
                    inputProps={{
                      'aria-label': `Subgroup for ${pair.pairName}`
                    }}
                  >
                    <MenuItem value={UNASSIGNED} sx={{ fontSize: '0.75rem' }}>
                      Unassigned
                    </MenuItem>
                    {sortedSubgroups.map(s => (
                      <MenuItem
                        key={s._id}
                        value={s._id}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {(assignMutation.isLoading || bulkAssignMutation.isLoading) && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ p: 0.5 }}>
          <CircularProgress size={12} />
          <Typography sx={{ fontSize: '0.7rem', color: 'grey.400' }}>
            Saving assignment…
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
